import { createSelector } from 'reselect'

import { outOfOrderSearch } from '../helpers/outOfOrderSearch'
import { sortEachCategory } from './sortEachCategory'

// Other selector dependencies
import {
  allNotesSelector,
  notesByCategorySelector,
  stringifiedNotesByIdSelector,
} from './notesFirstOrder'
import {
  isNotesManuallySortedSelector,
  noteFilterIsEmptySelector,
  noteFilterSelector,
  noteSortSelector,
  notesSearchTermSelector,
} from './secondOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Note} Note
 */

/**
 * @template A
 * @typedef {import('../index').CategorisedEntities<A>} CategorisedEntities
 */

/**
 * @type {function(PlottrState): Record<String, Array<Note>>}
 */
export const visibleSortedNotesByCategorySelector = createSelector(
  allNotesSelector,
  notesByCategorySelector,
  noteFilterSelector,
  noteFilterIsEmptySelector,
  noteSortSelector,
  isNotesManuallySortedSelector,
  (allNotes, notesByCategory, filter, filterIsEmpty, sort, isManuallySorted) => {
    if (!allNotes.length) return {}

    let visible = notesByCategory
    if (!filterIsEmpty && filter !== null) {
      visible = {}
      allNotes.forEach((n) => {
        const matches = Object.keys(filter).some((attr) => {
          if (attr === 'tag' || attr === 'character' || attr === 'place') {
            /**
             * @type {Array<number>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number>}
             */
            const noteValues =
              attr === 'tag'
                ? n.tags
                : attr === 'character'
                  ? n.characters
                  : attr === 'place'
                    ? n.places
                    : []
            return filterValues.some((fId) => {
              return noteValues.some((nId) => {
                return fId === nId
              })
            })
          } else if (attr === 'categoryId') {
            return filter[attr].some((cId) => {
              return cId === n.categoryId
            })
          } else if (attr === 'category') {
            return filter[attr]?.some?.((cId) => {
              return String(cId) === n.categoryId
            })
          } else if (attr === 'book') {
            /**
             * @type {Array<number | 'series'>}
             */
            const filterValues = filter[attr] ?? []
            /**
             * @type {Array<number | 'series'>}
             */
            const noteValues = n.bookIds
            return filterValues.some((fId) => {
              return noteValues.some((nId) => {
                return fId === nId
              })
            })
          } else {
            /**
             * @type {Array<String>}
             */
            const values = filter[attr]
            return values.some((val) => {
              return (val === '' && (!n[attr] || n[attr] == '')) || (n[attr] && n[attr] == val)
            })
          }
        })
        if (matches) {
          const categoryId = n.categoryId === undefined ? null : n.categoryId
          if (visible[`${categoryId}`] && visible[`${categoryId}`].length) {
            visible[`${categoryId}`].push(n)
          } else {
            visible[`${categoryId}`] = [n]
          }
        }
      })
      // @ts-ignore
      if (visible[undefined] !== undefined) {
        // @ts-ignore
        visible[null] = visible[undefined]
        // @ts-ignore
        delete visible[undefined]
      }
    }

    return sortEachCategory(visible, sort, isManuallySorted, null, null)
  }
)

/**
 * @type {function(PlottrState): Record<String, Array<Note>>}
 */
export const visibleSortedSearchedNotesByCategorySelector = createSelector(
  visibleSortedNotesByCategorySelector,
  notesSearchTermSelector,
  stringifiedNotesByIdSelector,
  (noteCategories, searchTerm, stringifiedNotes) => {
    if (!searchTerm) {
      return noteCategories
    } else {
      const lowSearchTerms = searchTerm
        .toLowerCase()
        .split(' ')
        .filter((x) => x)
      return Object.entries(noteCategories).reduce((acc, nextCategory) => {
        const [key, notes] = nextCategory
        const newNotes = notes.filter(({ id }) => {
          return outOfOrderSearch(lowSearchTerms, stringifiedNotes[id])
        })
        if (newNotes.length > 0) {
          return {
            ...acc,
            [key]: newNotes,
          }
        } else {
          return acc
        }
      }, {})
    }
  }
)
